/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Link as ReactRouterLink } from 'react-router';

import type { EnterpriseLearnerInsights_MetricTimeFrame as MetricTimeframe } from '__generated__/graphql-types';

import {
  Grid,
  InlineNotification,
  Link,
  Skeleton,
  Tab,
  TabContext,
  TabPanel,
  Tabs,
  Typography2,
  breakpoints,
  typography2,
} from '@coursera/cds-core';

import { Table, Td, Th, Thead, Tr } from 'bundles/admin-shared/components/styledTableComponents';
import EmptySkillsCardBody from 'bundles/enterprise-admin-home/components/EmptySkillsCardBody';
import EmptyTrackedSkillsMessage from 'bundles/enterprise-admin-home/components/EmptyTrackedSkillsMessage';
import LearningInsightsCard from 'bundles/enterprise-admin-home/components/LearningInsightsCard';
import LearningInsightsPercentageArrow from 'bundles/enterprise-admin-home/components/LearningInsightsPercentageArrow';
import useTrackedSkills from 'bundles/enterprise-admin-home/queries/useTrackedSkills';
import { convertMetricTimeFrameToInt } from 'bundles/enterprise-admin-home/utils/LearningInsightsUtils';
import EnterprisePermissionsRequired from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsRequired';
import { useIntl } from 'bundles/page/lib/useIntl';

import _t from 'i18n!nls/enterprise-admin-home';

import { TrackedSkillsHelp, TrendingSkillsHelp } from './TrendingSkillsHelp';

const styles = {
  tag: () => css`
    margin-left: var(--cds-spacing-100);
    text-transform: uppercase;
  `,
  notification: () => css`
    margin: var(--cds-spacing-300) 0;
  `,
  wrapper: () => css`
    ${breakpoints.up('lg')} {
      height: 100%;
    }
  `,
  header: () => css`
    margin-top: var(--cds-spacing-300);
  `,
  footerContainer: () => css`
    padding-bottom: var(--cds-spacing-200);

    ${breakpoints.only('xs')} {
      padding-bottom: var(--cds-spacing-150);
    }
  `,
  learnersAssessedCell: css`
    text-align: end;
    min-width: 200px;
  `,
  table: css`
    table-layout: auto !important;
    min-width: 600px;
  `,
  cellPadding: () => css`
    padding-top: var(--cds-spacing-300);
    padding-bottom: var(--cds-spacing-300);
  `,
  globalStrongTextOverride: () => css`
    font: ${typography2.bodyPrimary} !important;
    font-weight: 600 !important;
  `,
};

// Temp type that will be replaced when the graphql types are generated
type TrendingSkill = {
  rank: number;
  skillName: string;
  skillId: string;
  numOfLearners: number;
  changePercentage: number;
};

type Props = {
  hasError?: boolean;
  trendingSkills?: TrendingSkill[];
  orgSlug: string;
  orgId: string;
  timeframe: MetricTimeframe;
  isLoading?: boolean;
};

type SkillsTableProps = {
  hasError?: boolean;
  skills?: TrendingSkill[];
  orgSlug: string;
  timeframe: MetricTimeframe;
  isLoading?: boolean;

  description: JSX.Element;
  showRank?: boolean;
};
const SkillsTable = ({ skills, isLoading, hasError, orgSlug, timeframe, showRank, description }: SkillsTableProps) => {
  const intl = useIntl();

  const hasChangePercentages = skills?.some(
    (skill) => skill.changePercentage !== 0 && skill.changePercentage !== undefined
  );

  const numOfPrevDays = convertMetricTimeFrameToInt(timeframe);

  if (hasError) {
    return (
      <InlineNotification
        css={styles.notification}
        severity="error"
        title={_t('There was a problem fetching trending skills data')}
      >
        {_t('Please try refreshing the page or attempting again at a later time.')}
      </InlineNotification>
    );
  } else if (isLoading) {
    return (
      <div role="status">
        <Typography2 variant="subtitleMedium" component="h4" css={styles.header}>
          <Skeleton width={200} />
        </Typography2>
        <Table responsive={true} css={styles.table}>
          <Thead>
            <Tr>
              {showRank && <Th>{_t('Rank')}</Th>}
              <Th>{_t('Skill')}</Th>
              <Th css={styles.learnersAssessedCell}>
                {_t('# learners assessed learning,')}
                <br />
                <Typography2 variant="bodyPrimary" component="span">
                  <Skeleton width={215} />
                </Typography2>
              </Th>
            </Tr>
          </Thead>
          {[...Array(5)].map((index) => (
            <Tr key={index}>
              {showRank && (
                <Td css={styles.cellPadding}>
                  <Skeleton width={30} />
                </Td>
              )}
              <Td css={styles.cellPadding}>
                <Skeleton width={200} />
              </Td>
              <Td>
                <Grid container justifyContent="flex-end" spacing={4}>
                  <Grid container item xs={5} md={2} lg={5} justifyContent="flex-end">
                    <Skeleton width="100%" />
                  </Grid>
                  {/* Need to still render the grid to maintain the format if one skills has a change percentage and another doesn't */}
                  <Grid container item xs={5} md={3} lg={5} justifyContent="flex-end">
                    <Skeleton width="100%" />
                  </Grid>
                </Grid>
              </Td>
            </Tr>
          ))}
        </Table>
      </div>
    );
  } else if (!skills?.length) {
    return <EmptySkillsCardBody orgSlug={orgSlug} />;
  }

  return (
    <div>
      {description}
      <Table responsive={true} css={styles.table}>
        <Thead>
          <Tr>
            {showRank && <Th>{_t('Rank')}</Th>}
            <Th>{_t('Skill')}</Th>
            <Th css={styles.learnersAssessedCell}>
              {_t('# learners assessed learning')}
              {hasChangePercentages && ','}
              <br />
              {hasChangePercentages &&
                intl.formatMessage(_t('% change from prev. {numOfPrevDays} days'), { numOfPrevDays })}
            </Th>
          </Tr>
        </Thead>
        {skills.map((skill) => (
          <Tr key={skill.skillId}>
            {showRank && <Td css={styles.cellPadding}>{skill.rank}</Td>}
            <Td css={styles.cellPadding}>
              <strong css={styles.globalStrongTextOverride}>{skill.skillName}</strong>
            </Td>
            <Td>
              <Grid container justifyContent="flex-end" spacing={4}>
                <Grid
                  container
                  item
                  xs={hasChangePercentages ? 5 : 12}
                  md={hasChangePercentages ? 2 : 12}
                  lg={hasChangePercentages ? 5 : 12}
                  justifyContent="flex-end"
                >
                  <strong css={styles.globalStrongTextOverride}>{skill.numOfLearners}</strong>
                </Grid>
                <Grid container item xs={5} md={3} lg={5} justifyContent="flex-end">
                  <LearningInsightsPercentageArrow percentageOfChange={skill.changePercentage} />
                </Grid>
              </Grid>
            </Td>
          </Tr>
        ))}
      </Table>
    </div>
  );
};

const SKILL_TAB_TRENDING = 'trending';
const SKILL_TAB_TRACKED = 'tracked';

const TrendingSkillsCard = (props: Props) => {
  const [selectedTab, setSelectedTab] = React.useState(SKILL_TAB_TRENDING);

  const numOfPrevDays = convertMetricTimeFrameToInt(props.timeframe);
  const trackedSkills = useTrackedSkills(props.orgId, numOfPrevDays);

  return (
    <TabContext value={selectedTab}>
      <LearningInsightsCard
        customCss={styles.wrapper}
        header={_t('Skill Development')}
        timeframe={props.timeframe}
        footer={
          selectedTab === SKILL_TAB_TRENDING && props.trendingSkills?.length ? (
            <EnterprisePermissionsRequired
              domain="ORGANIZATION"
              domainId={props.orgId}
              subdomain="ANALYTICS"
              verb="VIEW"
            >
              <div css={styles.footerContainer}>
                <Link
                  typographyVariant="bodyPrimary"
                  variant="quiet"
                  to={`/o/${props.orgSlug}/admin/analytics/skills-development`}
                  component={ReactRouterLink}
                >
                  {_t('Explore skill development dashboard')}
                </Link>
              </div>
            </EnterprisePermissionsRequired>
          ) : (
            selectedTab === SKILL_TAB_TRACKED && (
              <EnterprisePermissionsRequired
                domain="ORGANIZATION"
                domainId={props.orgId}
                subdomain="ANALYTICS"
                verb="VIEW"
              >
                <div css={styles.footerContainer}>
                  <Link
                    typographyVariant="bodyPrimary"
                    variant="quiet"
                    to={`/o/${props.orgSlug}/admin/skills-tracking`}
                    component={ReactRouterLink}
                  >
                    {_t('View tracked skills')}
                  </Link>
                </div>
              </EnterprisePermissionsRequired>
            )
          )
        }
      >
        <EnterprisePermissionsRequired domain="ORGANIZATION" domainId={props.orgId} subdomain="SKILLSETS" verb="EDIT">
          <Tabs aria-label={_t('Skills Insights')}>
            <Tab
              label={_t('Trending Skills')}
              value={SKILL_TAB_TRENDING}
              onClick={() => setSelectedTab(SKILL_TAB_TRENDING)}
            />

            <Tab
              label={_t('Tracked Skills')}
              value={SKILL_TAB_TRACKED}
              onClick={() => setSelectedTab(SKILL_TAB_TRACKED)}
            />
          </Tabs>
        </EnterprisePermissionsRequired>

        <TabPanel value={SKILL_TAB_TRENDING}>
          <SkillsTable
            {...props}
            skills={props.trendingSkills}
            showRank
            description={
              <TrendingSkillsHelp numOfSkills={props.trendingSkills?.length ?? 0} numOfPrevDays={numOfPrevDays} />
            }
          />
        </TabPanel>

        <EnterprisePermissionsRequired domain="ORGANIZATION" domainId={props.orgId} subdomain="SKILLSETS" verb="EDIT">
          <TabPanel value={SKILL_TAB_TRACKED}>
            {trackedSkills.skills.length > 0 && (
              <SkillsTable
                {...props}
                {...trackedSkills}
                description={
                  <TrackedSkillsHelp numOfSkills={trackedSkills.skills.length} numOfPrevDays={numOfPrevDays} />
                }
              />
            )}
            {trackedSkills.skills.length === 0 && (
              <div css={styles.notification}>
                <Typography2 variant="subtitleMedium" component="p">
                  {_t('Monitor your key skills at a glance')}
                </Typography2>
                <Typography2 component="p">
                  {_t(
                    'Drive effective skill development by tracking progress on your most important skills in a single view available to you and your team.'
                  )}
                </Typography2>
                <EmptyTrackedSkillsMessage />
              </div>
            )}
          </TabPanel>
        </EnterprisePermissionsRequired>
      </LearningInsightsCard>
    </TabContext>
  );
};

export default TrendingSkillsCard;
